import {
  Box,
  Center,
  Flex,
  Pill,
  TextOverflow
} from '@sequencehq/core-components'
import { ContractReference } from 'Cube/domain/cube.domain.types'
import { FC, SyntheticEvent, useState } from 'react'
import { ContractReferenceModal } from './contractReference.modal'
import { CONTRACT_REFERENCE_TYPE_CONFIG } from './contractReferences.constants'
import { getReferenceAt } from './contractReferences.helpers'
import { ContractReferenceLink } from './ContractReferenceLink'
import { usePhaseContractReferences } from './usePhaseContractReferences'

/**
 * One pill per reference in the phase header. Clicking the pill changes the
 * reference, the cross removes it and the arrow follows its link. None of
 * their clicks reach the header, so they never expand or collapse the phase.
 */
export const ContractReferenceBadges: FC<{ phaseId: string }> = props => {
  const contractReferences = usePhaseContractReferences({
    phaseId: props.phaseId
  })
  const [changingIndex, setChangingIndex] = useState<number | null>(null)

  if (!contractReferences.isVisible || !contractReferences.references.length) {
    return null
  }

  const changingReference = getReferenceAt(
    contractReferences.allReferences,
    changingIndex ?? undefined
  )

  return (
    <Flex
      alignItems="center"
      justifyContent="flex-end"
      flexWrap="wrap"
      gap="6px"
      // Capped so the title and menu keep their room, but wide enough for two
      // full-width pills and their gap (2 x 200 + 6). Pills that don't fit
      // wrap onto another row, so every reference stays reachable.
      maxWidth="406px"
      // Keeps wrapped rows off the header's edges. A single row still fits in
      // the header's 48px, so phases with few references look as they did.
      paddingY="8px"
      onClick={e => e.stopPropagation()}
      data-testid="phase.contractReferences"
    >
      {contractReferences.references.map(({ reference, index }) => (
        <ContractReferenceBadge
          key={`${reference.type}-${index}`}
          reference={reference}
          canEdit={contractReferences.canEdit}
          onChange={() => setChangingIndex(index)}
          onRemove={() => contractReferences.removeReference(index)}
          data-testid={`phase.contractReferences.badge.${index}`}
        />
      ))}
      {changingIndex !== null && changingReference && (
        <ContractReferenceModal
          phaseId={props.phaseId}
          type={changingReference.type}
          index={changingIndex}
          onClose={() => setChangingIndex(null)}
        />
      )}
    </Flex>
  )
}

const ContractReferenceBadge: FC<{
  reference: ContractReference
  canEdit: boolean
  onChange: () => void
  onRemove: () => void
  'data-testid': string
}> = props => {
  const { label: typeLabel, Icon } =
    CONTRACT_REFERENCE_TYPE_CONFIG[props.reference.type]

  return (
    // A long label truncates rather than taking a whole row.
    <Box maxWidth="200px">
      <Pill
        icon={
          <Center
            title={typeLabel}
            data-testid={`${props['data-testid']}.icon`}
          >
            <Icon />
          </Center>
        }
        interactive={props.canEdit}
        onClick={props.canEdit ? props.onChange : undefined}
        onClear={
          props.canEdit
            ? (e: SyntheticEvent) => {
                // The pill itself opens the change dialog, so a click on its
                // cross must not reach it.
                e.stopPropagation()
                props.onRemove()
              }
            : undefined
        }
        aria-label={`Change ${typeLabel.toLowerCase()} ${props.reference.label}`}
        data-testid={props['data-testid']}
      >
        <Flex alignItems="center" gap="4px" minWidth="0">
          <TextOverflow>{props.reference.label}</TextOverflow>
          <ContractReferenceLink
            reference={props.reference}
            data-testid={`${props['data-testid']}.link`}
          />
        </Flex>
      </Pill>
    </Box>
  )
}
